<script setup lang="ts">
import { type Component, computed } from "vue";

import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import DropdownMenuItemContent from "~/components/dumb/DropdownMenuItemContent.vue";
import { GROUP_BY_LAYOUT_KINDS } from "~/constants/layout";
import { ChevronDownIcon, GroupByIcon } from "~/icons";
import { DropdownMenuItemKind, LayoutKind, Placement } from "~/shared/enums";
import { useAppStore, usePageStore } from "~/stores";

const props = defineProps<{
  layoutKind: LayoutKind;
  groupBy: string;
}>();

const emit = defineEmits<{
  setGroupBy: [groupBy: string];
}>();

const appStore = useAppStore();
const pageStore = usePageStore();

const groupByEnabled = computed(() => GROUP_BY_LAYOUT_KINDS.has(props.layoutKind));
const groupByNameAndIcon = computed<[string, Component] | null>(() => {
  const option = appStore.groupByDefinitionList.find((e) => e.property.duid === props.groupBy);
  if (!option) {
    return null;
  }
  return [option.property.title, option.icon];
});
const groupBySections = computed(() => [
  {
    title: "Group by",
    items: appStore.groupByDefinitionList.map((option) => ({
      title: option.property.title,
      icon: option.icon,
      kind: DropdownMenuItemKind.BUTTON,
      disabled: option.property.duid === props.groupBy,
      onClick: () => emit("setGroupBy", option.property.duid),
    })),
  },
]);
</script>

<template>
  <template v-if="groupByEnabled && groupByNameAndIcon">
    <div
      v-if="!pageStore.isMobile"
      class="flex w-full select-none items-center justify-between gap-4 rounded px-2 py-1">
      <div class="whitespace-nowrap">Group by</div>
      <DropdownMenu
        :sections="groupBySections"
        :placement="Placement.BOTTOM_RIGHT"
        :width-pixels="192"
        :distance="2"
        prevent-close-on-select>
        <div
          class="flex w-48 items-center justify-between overflow-hidden rounded border px-1 py-0.5 border-lt hover:bg-lt">
          <div class="flex items-center gap-2">
            <component :is="groupByNameAndIcon[1]" class="text-vlt icon-sm" />
            <div class="truncate" :title="groupByNameAndIcon[0]">{{ groupByNameAndIcon[0] }}</div>
          </div>

          <ChevronDownIcon class="text-vlt icon-sm" />
        </div>
      </DropdownMenu>
    </div>
    <DropdownMenu v-else :sections="groupBySections" header-title="Group by" show-search class="py-2">
      <DropdownMenuItemContent
        :icon="GroupByIcon"
        title="Group by"
        class="text-md"
        is-submenu
        :right-text="groupByNameAndIcon[0]"
        right-text-style="text-vlt" />
    </DropdownMenu>
  </template>
</template>
